import { Injectable } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client/core';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { finalize } from 'rxjs/operators';

import {
  DataAccessService,
  GetDefaultPhasesDocument,
  GetDefaultPhasesQuery,
  GetDefaultPhaseByIdQuery,
  GetDefaultPhaseByIdDocument,
  FetchDefaultPhaseTipsCountDocument
} from '@tripointe/tripointe-cms/data-access';
import { TabWithDropdown } from '@tripointe/tripointe-cms/types';

@Injectable({
  providedIn: 'root'
})
export class CommonDefaultPhasesService {
  loading$ = new BehaviorSubject(false);
  defaultPhaseId$: BehaviorSubject<number> = new BehaviorSubject(null);
  defaultPhaseTipsListStatsCount$: BehaviorSubject<TabWithDropdown[]> = new BehaviorSubject([
    {
      name: 'phase tips',
      statsCount: null,
      archived: false
    }
  ]);
  defaultPhaseTipsListArchivedStatus$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(private dataAccess: DataAccessService) {}

  setLoading(isLoading: boolean): void {
    this.loading$.next(isLoading);
  }

  getDefaultPhaseById(
    defaultPhaseId: number
  ): Observable<ApolloQueryResult<GetDefaultPhaseByIdQuery>> {
    this.setLoading(true);

    return this.dataAccess
      .get<GetDefaultPhaseByIdQuery>(GetDefaultPhaseByIdDocument, { defaultPhaseId })
      .pipe(finalize(() => this.setLoading(false)));
  }

  getDefaultPhaseTipsStats(defaultPhaseId: number, archivedStatus: boolean): void {
    this.dataAccess
      .get(FetchDefaultPhaseTipsCountDocument, { defaultPhaseId })
      .subscribe((res: ApolloQueryResult<any>) => {
        const stats = res.data['fetchDefaultPhaseTipsCount'];
        this.setDefaultPhaseTipsListStatsCount([
          {
            name: 'phase tips',
            archived: archivedStatus,
            statsCount: stats.statsCount
          }
        ]);
      });
  }

  setDefaultPhaseTipsListStatsCount(newStats: TabWithDropdown[]): void {
    this.defaultPhaseTipsListStatsCount$.next(newStats);
  }

  getPhaseTipsListStatsCount(): Observable<TabWithDropdown[]> {
    return this.defaultPhaseTipsListStatsCount$.asObservable();
  }

  setPhaseTipsListArchivedStatus(newStatus: boolean): void {
    this.defaultPhaseTipsListArchivedStatus$.next(newStatus);
  }

  getPhaseTipsListArchivedStatus(): Observable<boolean> {
    return this.defaultPhaseTipsListArchivedStatus$.asObservable();
  }

  getDefaultPhases(archived?: boolean): Observable<ApolloQueryResult<GetDefaultPhasesQuery>> {
    this.setLoading(true);

    return this.dataAccess
      .get<GetDefaultPhasesQuery>(GetDefaultPhasesDocument, { archived })
      .pipe(finalize(() => this.setLoading(false)));
  }
}
